import BaseEntity             from '@/core/misc/BaseEntity';
import Core                   from '@/core/Core';
import serializeSimplified    from '@/core/misc/decorators/SerializeSimplified';
import Title_ru_RU_Class      from "@/core/misc/decorators/Title_ru_RU_Class";
import Title_ru_RU            from '@/core/misc/decorators/Title_ru_RU';
import TYPES                  from '@/core/services/Changelogs/types';
import TransportZone          from '@/domains/TransportZones/TransportZone';
import Price                  from "@/core/types/Price";
import Currency               from "@/domains/Currencies/Currency";
import CurrenciesService      from "@/domains/Currencies/CurrenciesService";
import NoSerialize            from "@/core/misc/decorators/NoSerialize";
import Vehicle                from "@/domains/Vehicles/Vehicle"
import VehicleType            from "@/domains/VehicleTypes/VehicleType"
import Partner                from '@/domains/Partners/Partner';
import TransportRouteTariff   from '@/domains/TransportRoutes/TransportRouteTariff';
import DateSimple             from '@/core/types/DateSimple';
import Weight                 from '@/core/types/Weight';
import Volume                 from '@/core/types/Volume';

/**
 * Маршрут доставки между двумя транспортными зонами.
 * Хранит цены за кг и за м³ в валюте costCurrencyCode.
 *
 * Цены отдаются как есть, в валюте тарифа: сам Price валюту не несёт, а
 * приводить её к валюте потребителя маршрут не обязан. Конвертацию делает
 * потребитель — ему известны и целевая валюта, и дата курса (обычно
 * AbstractOrderOption.getDateForCurrencyRate()).
 */
@Title_ru_RU_Class('Маршрут')
class TransportRoute extends BaseEntity {

  static Events = {
    Created: 'TransportRoute:created',
    Updated: 'TransportRoute:updated',
    Deleted: 'TransportRoute:deleted',
  } as const;

  @Title_ru_RU('Зона отправления', TYPES.OBJECT)
  @serializeSimplified
  public zoneFrom: TransportZone;

  @Title_ru_RU('Зона назначения', TYPES.OBJECT)
  @serializeSimplified
  public zoneTo: TransportZone;

  @Title_ru_RU('Валюта тарифа', TYPES.STRING)
  protected _costCurrencyCode: string | null = null;

  /** Валюта тарифа объектом. Выводится из кода, на бек не уезжает — там
   *  хранится код. */
  @NoSerialize
  protected _costCurrency: Currency | null = null;

  @Title_ru_RU('Цена за кг', TYPES.NUMBER)
  public costPerKg: Price | null;

  @Title_ru_RU('Цена за м³', TYPES.NUMBER)
  public costPerM3: Price | null;

  @Title_ru_RU('Транспорт', TYPES.OBJECT)
  public vehicle: Vehicle | null = null;

  @Title_ru_RU('Тип транспорта', TYPES.OBJECT)
  public vehicleType: VehicleType | null = null;

  @Title_ru_RU('Ставка за рейс', TYPES.PRICE)
  public ftlCost: Price | null = null;

  @Title_ru_RU('Перевозчик', TYPES.OBJECT)
  @serializeSimplified
  public deliverer: Partner | null;

  /** null — маршрут платформы. Задаётся при создании и не меняется */
  @Title_ru_RU('Владелец', TYPES.OBJECT)
  @serializeSimplified
  public owner: Partner | null;

  /** Основной перевозчик пары зон: по нему считается доставка */
  @Title_ru_RU('Основной перевозчик', TYPES.BOOLEAN)
  public isMain: boolean;

  @Title_ru_RU('Тарифы', TYPES.TARIFFS)
  public tariffs: TransportRouteTariff[];


  get costCurrencyCode(): string | null {
    return this._costCurrencyCode;
  }

  /** Код — единственный источник истины: объект валюты выводится здесь,
   *  поэтому потребителям не нужен переходник вида computed { get, set }. */
  set costCurrencyCode(value: string | null) {
    this._costCurrencyCode = value;

    if (this._costCurrencyCode) {
      this._costCurrency = CurrenciesService.currencies.get(this._costCurrencyCode) ?? null;
    } else {
      this._costCurrency = null;
    }
  }

  get costCurrency(): Currency | null {
    return this._costCurrency;
  }

  /** Пишем через сеттер кода, чтобы согласованность держалась в одном месте. */
  set costCurrency(value: Currency | null) {
    this.costCurrencyCode = value?.code ?? null;
  }

  /**
   * Гидратирует маршрут из данных сервера
   */
  static fromServer(data: any, result = new TransportRoute()): TransportRoute {
    super.fromServer(data, result);

    result.zoneFrom = Core.utils.hydrate(data.zoneFrom, TransportZone.fromServer)!;
    result.zoneTo = Core.utils.hydrate(data.zoneTo, TransportZone.fromServer)!;

    result.costPerKg = Core.utils.hydrate(data.costPerKg, Price.fromServer);
    result.costPerM3 = Core.utils.hydrate(data.costPerM3, Price.fromServer);

    /** Через сеттер, а не в приватное поле: он же выводит объект валюты. */
    result.costCurrencyCode = data.costCurrencyCode ?? null;

    result.vehicle = Core.utils.hydrate(data.vehicle, Vehicle.fromServer);
    result.vehicleType = Core.utils.hydrate(data.vehicleType, VehicleType.fromServer);

    result.ftlCost = Core.utils.hydrate(data.ftlCost, Price.fromServer)

    result.deliverer = Core.utils.hydrate(data.deliverer, Partner.fromServer);
    result.owner     = Core.utils.hydrate(data.owner, Partner.fromServer);
    result.isMain    = data.isMain ?? false;
    result.tariffs   = Core.utils.hydrateArray(data.tariffs, TransportRouteTariff.fromServer);

    return result;
  }

  /**
   * Создаёт новый пустой маршрут с валютой текущего партнёра по умолчанию
   */
  static fromNew(): TransportRoute {
    const route = new this();

    route.costCurrencyCode = Core.currentPartner.country.localCurrency.code;
    route.deliverer = null;
    route.owner     = null;
    route.isMain    = false;
    route.tariffs   = [];

    return route;
  }

  /** Перевозка целой машиной: рейсовая ставка и сетка тарифов взаимоисключают друг друга */
  get isFtl(): boolean {
    return this.vehicleType !== null || this.vehicle !== null || this.ftlCost?.isPositive() === true;
  }

  get hasTariffs(): boolean {
    return this.tariffs.length > 0;
  }

  /** Даты начала сеток, новые сверху. Сетка — строки с одной датой начала */
  get tariffStartDates(): DateSimple[] {
    const result: DateSimple[] = [];

    this.tariffs.forEach((tariff) => {
      const tariffStart = tariff.validityPeriod.start;

      if (tariffStart !== null && result.some(start => start.equals(tariffStart)) === false) {
        result.push(tariffStart);
      }
    });

    return result.sort((left, right) => right.compareTo(left));
  }

  /** Строки сетки по возрастанию веса, а без веса — объёма: как в прайсе перевозчика */
  getTariffsByStart(start: DateSimple): TransportRouteTariff[] {
    return this.tariffs
      .filter(tariff => tariff.validityPeriod.start?.equals(start) === true)
      .sort((left, right) => {
        if (left.hasWeight === true && right.hasWeight === true) {
          return left.weight!.compare(right.weight);
        }
        else if (left.hasVolume === true && right.hasVolume === true) {
          return left.volume!.compare(right.volume);
        }
        else {
          return 0;
        }
      });
  }

  /** Действующая сетка: самая поздняя дата начала не позже сегодня. Истёкшая продолжает действовать, пока нет новой */
  get actualTariffStart(): DateSimple | null {
    return this.getActualTariffStart(DateSimple.today());
  }

  getActualTariffStart(date: DateSimple): DateSimple | null {
    return this.tariffStartDates.find(start => start.isAfter(date) === false) ?? null;
  }

  /**
   * Стоимость перевозки партии по тарифной сетке на дату, в валюте маршрута — зеркало бэка TransportRoute::calculateCost.
   * По весу и по объёму считается отдельно, берётся то, что дороже — так считают перевозчики.
   * null — сетки на дату нет
   */
  calculateCost(weight: Weight, volume: Volume, date: DateSimple): Price | null {
    const start = this.getActualTariffStart(date);
    const tariffs = this.tariffs.filter(tariff => start !== null && tariff.validityPeriod.start?.equals(start) === true);
    let result: Price | null;

    const costByWeight = TransportRoute.calculateCostByStep(
      tariffs.filter(tariff => tariff.weight !== null),
      tariff => tariff.weight!,
      weight,
    );
    const costByVolume = TransportRoute.calculateCostByStep(
      tariffs.filter(tariff => tariff.volume !== null),
      tariff => tariff.volume!,
      volume,
    );

    if (costByWeight === null || (costByVolume !== null && costByVolume.greaterThan(costByWeight) === true)) {
      result = costByVolume;
    }
    else {
      result = costByWeight;
    }

    return result;
  }

  /**
   * Ступенька: строка — последняя, чей порог не больше партии, ставка строки (стоимость / порог) × партия.
   * Партия меньше первой строки — стоимость первой строки как минимальная
   */
  private static calculateCostByStep<T extends Weight | Volume>(
    tariffs: TransportRouteTariff[],
    getThreshold: (tariff: TransportRouteTariff) => T,
    amount: T,
  ): Price | null {
    const sorted = [...tariffs].sort((left, right) => getThreshold(left).compare(getThreshold(right)));
    let result: Price | null = null;

    if (sorted.length > 0) {
      if (amount.lessThan(getThreshold(sorted[0])) === true) {
        result = sorted[0].cost;
      }
      else {
        let step = sorted[0];

        sorted.forEach((tariff) => {
          if (getThreshold(tariff).greaterThan(amount) === false) {
            step = tariff;
          }
        });

        result = Price.fromOperation(step.cost.value.times(amount.value).dividedBy(getThreshold(step).value));
      }
    }

    return result;
  }

  /**
   * Отображаемое имя маршрута: «Зона А — Зона Б»
   */
  public get name(): string {
    const from = this.zoneFrom?.name ?? this.zoneFrom?.id ?? '?';
    const to   = this.zoneTo?.name   ?? this.zoneTo?.id   ?? '?';
    return `${from} — ${to}`;
  }

  toString(): string {
    return this.name;
  }

  /**
   * Валидирует маршрут перед сохранением
   */
  validate(): string[] {
    const errors: string[] = [];

    if (!this.zoneFrom?.id) {
      errors.push('Укажите зону отправления');
    }
    if (!this.zoneTo?.id) {
      errors.push('Укажите зону назначения');
    }
    if (this.zoneFrom?.id && this.zoneTo?.id && this.zoneFrom.id === this.zoneTo.id) {
      errors.push('Зоны отправления и назначения должны различаться');
    }
    /* общая цена нужна, только пока нет тарифов перевозчика */
    if (this.hasTariffs === false) {
      if (!this.costPerKg?.isPositive()) {
        errors.push('Укажите цену за кг');
      }
      if (!this.costPerM3?.isPositive()) {
        errors.push('Укажите цену за м³');
      }
    }
    if (!this.costCurrencyCode) {
      errors.push('Не задана валюта тарифа');
    }

    this.tariffs.forEach((tariff) => {
      tariff.validate().forEach((error) => {
        if (errors.includes(error) === false) {
          errors.push(error);
        }
      });
    });

    return errors;
  }
}

export default TransportRoute;
